import { useDispatch } from 'react-redux'

import { mergeParams } from '@procsea/common/utils'

import { dispatchCreateNotification } from 'src/action-creators'
import { useUserBuyerCompanyId } from 'src/contexts/userContext'
import { useSegment } from 'src/hooks'
import { usePatchEntityMutation, usePostEntityMutation } from 'src/queries/entity'
import {
  GenerateQueriesArgs,
  UsePaginatedEntitiesQueryArgs,
  generateQueries,
} from 'src/queries/queryGenerator'
import { BuyerTour } from 'src/types/tour'

export const TOURS_ENDPOINT = 'buyer/tours'
export const TOURS_QUERY_KEY = 'tours'

export interface PaginatedQueryParams {
  limit?: number
  offset?: number
}

export interface BuyerTourQueryParams {
  archived?: boolean
  buyerCompanyId?: Id
  sellerCompanyId?: Id | Id[]
}

export type GetPaginatedBuyerToursQueryParams = BuyerTourQueryParams & PaginatedQueryParams

const generateQueriesConfig: GenerateQueriesArgs<BuyerTour, GetPaginatedBuyerToursQueryParams> = {
  endpoint: TOURS_ENDPOINT,
  queryKey: TOURS_QUERY_KEY,
  usePaginatedEntitiesQueryArgs: {
    queryOptions: { cacheTime: 1000 * 60 * 60, staleTime: 1000 * 60 * 60 },
  },
}

const { useSingleEntityQuery, usePaginatedEntitiesQuery, useRecursiveQuery } =
  generateQueries(generateQueriesConfig)

type UsePaginatedBuyerToursQueryArgs<TData> = UsePaginatedEntitiesQueryArgs<
  BuyerTour,
  Partial<GetPaginatedBuyerToursQueryParams>,
  TData
>

export const usePaginatedBuyerToursQuery = <TData = BuyerTour>(
  args: UsePaginatedBuyerToursQueryArgs<TData> = {}
) => {
  const buyerCompanyId = useUserBuyerCompanyId()
  const params: UsePaginatedBuyerToursQueryArgs<TData> = {
    queryOptions: { enabled: !!buyerCompanyId },
    queryParams: { buyerCompanyId },
  }

  return mergeParams(usePaginatedEntitiesQuery)(params)(args)
}

export const useRecursiveBuyerToursQuery = <TData = BuyerTour>(
  args: UsePaginatedBuyerToursQueryArgs<TData> = {}
) => {
  const buyerCompanyId = useUserBuyerCompanyId()
  const params: UsePaginatedBuyerToursQueryArgs<TData> = {
    queryOptions: { enabled: !!buyerCompanyId },
    queryParams: { buyerCompanyId },
  }

  return mergeParams(useRecursiveQuery)(params)(args)
}

export const useBuyerTourQuery = useSingleEntityQuery

export interface PostBuyerTourPayload {
  buyerCompanyId: Id
  name: string
}

export const usePostBuyerTourMutation = () => {
  const dispatch = useDispatch()
  const { track } = useSegment()

  return usePostEntityMutation<PostBuyerTourPayload, BuyerTour>({
    endpoint: TOURS_ENDPOINT,
    queryOptions: {
      onSuccess: newTour => {
        dispatchCreateNotification(dispatch)({
          message: gettext('The tour has been successfully created'),
          type: 'positive',
        })

        track('Buyer - Tour Created', {
          tourId: newTour.id,
        })
      },
    },
  })
}

export interface ArchiveBuyerTourPayload {
  archived: true
  id: Id
}

export const useArchiveBuyerTourMutation = () => {
  const dispatch = useDispatch()
  const { track } = useSegment()

  return usePatchEntityMutation<ArchiveBuyerTourPayload, BuyerTour>({
    endpoint: TOURS_ENDPOINT,
    queryOptions: {
      onSuccess: (_responseBody, payload) => {
        dispatchCreateNotification(dispatch)({
          message: gettext('The tour has been successfully archived'),
          type: 'positive',
        })

        track('Buyer - Tour Archived', {
          tourId: payload.id,
        })
      },
    },
  })
}
